import React from 'react';
import { action, observable, reaction, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import { debounce, get } from 'lodash';
import { Classes } from '@blueprintjs/core';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';
import classNames from 'classnames';

import getScrollbarWidth from 'core/util/getScrollbarWidth';
import { formAriaProps } from 'core/util/ariaUtils';

import GroupRow from './GroupRow';
import Header from './Header';
import Row from './Row';
import StickyHeader from './StickyHeader';
import TotalRow from './TotalRow';
import { TableRaw } from './Table';
import VirtualizedVisibleRowsObserver from './virtualizedVisibleRowsObserver';

export const STICKY_TOTAL_ROW_HEIGHT = 36;

/**
 * @typedef {Object} VisibleRowsChangePayload
 * @property {number} startIndex
 * @property {number} stopIndex
 * @property {Array<object>} visibleModels
 */

/**
 * @callback OnVisibleRowsChange
 * @param {VisibleRowsChangePayload} payload
 * @returns {void}
 */

/**
 * @typedef {Object} VirtualizedTableProps
 * @property {object} collection
 * @property {Array<object>} columns
 * @property {boolean} [infiniteScroll]
 * @property {number|function} [rowHeight]
 * @property {boolean} [stickyHeader]
 * @property {boolean} [hideHeader]
 * @property {boolean} [noHighlightSorted]
 * @property {boolean} [useSortTooltips]
 * @property {boolean} [selectOnRowClick]
 * @property {boolean} [minimal]
 * @property {string} [modelField]
 * @property {string} [rowKeyField]
 * @property {object} [resizedColumns]
 * @property {object} [columnWidthsRef]
 * @property {string} [tabPrefId]
 * @property {object} [headerStyle]
 * @property {boolean} [flexed]
 * @property {number} [scrollToIndex]
 * @property {'auto'|'start'|'center'|'end'} [scrollToAlignment]
 * @property {boolean} [showTotalRow]
 * @property {boolean} [stickyTotalRow]
 * @property {function} [onRowClick]
 * @property {function} [onSort]
 * @property {boolean} [persistColumnWidths]
 * @property {OnVisibleRowsChange} [onVisibleRowsChange]
 */

/**
 * Virtualized table implementation with infinite loading and grouped rows support.
 * @extends TableRaw
 */
@inject('$setup')
@observer
export default class VirtualizedTable extends TableRaw {
  constructor(props) {
    super(props);
    this.virtualizedVisibleRowsObserver = new VirtualizedVisibleRowsObserver();
  }

  @observable.ref
  groupedRows = [];

  // tracks group keys so we can ensure collapsed state for new rows when using the isCollapsed prop
  seenGroupKeys = new Set();

  internalColumnWidthsRef = null;

  internalResizedColumns = null;

  columnWidthsInitialized = false;

  isRenderingVisibleRows = false;

  @observable
  stickyHeaderProps = {};

  stickyHeaderModel = null;

  static defaultProps = {
    noHighlightSorted: false,
    useSortTooltips: false,
    // style object passed to Header directly
    headerStyle: {},
    infiniteScroll: false,
    minimal: false,
    rowHeight: 52,
    breakpoint: 1024,
    selectOnRowClick: true,
    stickyHeader: false,
    hideHeader: false,
    resizedColumns: {},
    tableLabelId: null
  };

  // This links functions on the collection to a table function
  setupReactions(collection) {
    this.filterDisposer = reaction(() => collection.filterState, this.updateGroupedRows, { delay: 50 });
    this.presetFilterDisposer = reaction(() => collection.activePresetFilter, this.updateGroupedRows, { delay: 50 });
    this.sortDisposer = reaction(() => collection.sortState, this.updateGroupedRows, { delay: 50 });
    this.groupByDisposer = reaction(() => collection.groupBy, this.updateGroupedRows);
    this.groupCollapseDisposer = reaction(() => this.collapsedRows.entries(), this.updateGroupedRows);
    this.lastUpdatedDisposer = reaction(() => collection.lastUpdated, this.updateGroupedRows);
    this.selectedDisposer = reaction(() => collection.selected, this.updateGroupedRows);
    this.discreteFilterDisposer = reaction(() => collection.discreteFilters, this.updateGroupedRows, {
      delay: 50
    });

    if (collection.groupBy) {
      this.updateGroupedRows();
    }
  }

  clearDisposers() {
    this.filterDisposer();
    this.presetFilterDisposer();
    this.sortDisposer();
    this.groupByDisposer();
    this.groupCollapseDisposer();
    this.lastUpdatedDisposer();
    this.selectedDisposer();
    this.discreteFilterDisposer();
  }

  componentDidMount() {
    const { collection, onVisibleRowsChange } = this.props;
    this.setupReactions(collection);
    this.virtualizedVisibleRowsObserver.update({ onVisibleRowsChange });
    super.componentDidMount();
  }

  ensureColumnWidthsInitialized() {
    if (this.columnWidthsInitialized) {
      return;
    }
    this.columnWidthsInitialized = true;

    const { $setup, tabPrefId } = this.props;

    if (tabPrefId && !this.props.columnWidthsRef) {
      const savedResizedColumns = get(toJS($setup.settings), `tabPrefs.resizedColumns.${tabPrefId}`) || {};
      this.internalResizedColumns = savedResizedColumns;
      this.internalColumnWidthsRef = { ...savedResizedColumns };
    } else {
      this.internalResizedColumns = {};
      this.internalColumnWidthsRef = {};
    }
  }

  getResizedColumns() {
    this.ensureColumnWidthsInitialized();
    return this.props.columnWidthsRef ? this.props.resizedColumns : this.internalResizedColumns;
  }

  getColumnWidthsRef() {
    this.ensureColumnWidthsInitialized();
    return this.props.columnWidthsRef || this.internalColumnWidthsRef;
  }

  shouldRenderStickyTotals() {
    const { stickyTotalRow, showTotalRow, collection } = this.props;
    return (
      stickyTotalRow &&
      showTotalRow &&
      collection &&
      !collection.isRequestActive('fetching') &&
      collection.models.length > 0
    );
  }

  getContentHeight(height) {
    return this.shouldRenderStickyTotals() ? Math.max(0, height - STICKY_TOTAL_ROW_HEIGHT) : height;
  }

  getStickyFooterHeight() {
    return STICKY_TOTAL_ROW_HEIGHT;
  }

  componentDidUpdate(prevProps) {
    const { collection, onVisibleRowsChange } = this.props;
    if (collection !== prevProps.collection) {
      this.clearDisposers();
      this.setupReactions(collection);
      this.updateGrid();
    }

    if (onVisibleRowsChange !== prevProps.onVisibleRowsChange) {
      this.virtualizedVisibleRowsObserver.update({ onVisibleRowsChange });
    }
  }

  componentWillUnmount() {
    this.clearDisposers();
    super.componentWillUnmount();
  }

  listRefHandler = (ref) => {
    if (ref) {
      this.listRef = ref;
    }
  };

  @action
  updateGrid = () => {
    if (this.listRef && this.listRef.Grid) {
      this.listRef.recomputeRowHeights();
      this.listRef.forceUpdateGrid();
    }
  };

  @action
  updateGroupedRows = () => {
    const { collection, isCollapsed } = this.props;
    const groupedRows = [];

    if (collection.isTreeGroupBy) {
      const groupedData = collection.getGroupedData(this.collapsedRows);

      for (let i = 0; i < groupedData.length; i += 1) {
        const model = groupedData[i];
        const index = model.get('index');

        groupedRows.push({
          model,
          isExpanded: !this.collapsedRows.has(index),
          hasChildren: model.hasChildren,
          onToggle: (e) => {
            e.stopPropagation();
            this.toggleGroup(index);
          }
        });
      }
    } else if (collection.groupBy) {
      const { groupedData, sortedGroupKeys } = collection;

      sortedGroupKeys.forEach((groupKey, idx, arr) => {
        if (isCollapsed && !this.seenGroupKeys.has(groupKey)) {
          // when we want a default collapsed state but haven't seen the current key yet, explicitly mark it as collapsed
          // from here on, the user can toggle at will
          this.collapsedRows.set(groupKey, true);
        }

        // mark the group as 'seen', enabling user-defined expand/collapse state
        this.seenGroupKeys.add(groupKey);

        const isExpanded = !this.collapsedRows.has(groupKey);

        groupedRows.push({
          groupKey,
          isGroupSummary: true,
          isExpanded,
          onToggle: () => this.toggleGroup(groupKey),
          last: idx === arr.length - 1
        });

        if (isExpanded) {
          groupedRows.push(...groupedData[groupKey].map((model) => ({ groupKey, model })));
        }
      });
    }

    this.groupedRows = groupedRows;

    this.updateGrid();
  };

  /**
   * @param {{index: number}} args
   * @returns {number}
   */
  getRowHeight = ({ index }) => {
    const { collection, rowHeight } = this.props;

    if (typeof rowHeight === 'number') {
      return rowHeight;
    }

    let model = index < collection.size ? collection.at(index) : null;
    if (collection.groupBy) {
      const row = this.getGroupedRow(index);
      // group summary row is not part of the collection
      model = row?.isGroupSummary ? row : row?.model;
    }
    return rowHeight({ index, model }, this);
  };

  /**
   * @param {number} index
   * @returns {object|null}
   */
  getGroupedRow = (index) =>
    // Returning null can occur when this.props.showTotalRow is true as it increases the rowCount
    index < this.groupedRows.length ? this.groupedRows[index] : null;

  @action
  setStickyHeader = debounce((model, props) => {
    this.stickyHeaderModel = model;
    this.stickyHeaderProps = props;
  }, 100);

  /**
   * @param {{index: number}} args
   * @returns {boolean}
   */
  isItemLoaded = ({ index }) => {
    const { collection } = this.props;
    const fullSize = parseInt(collection.totalCount);
    const itemModelPresent = collection.models.length - 1 >= index && !!collection.models[index];

    let collapseCount = 0;

    if (collection.groupBy) {
      for (const key of this.collapsedRows.keys()) {
        collapseCount += collection.groupedData?.[key]?.length || 0;
      }
    }

    return index + collapseCount < collection.size || (itemModelPresent && index <= fullSize);
  };

  /**
   * @param {{startIndex: number, stopIndex: number}} args
   * @returns {Promise<void>}
   */
  loadMoreItems = ({ startIndex, stopIndex }) => {
    const { collection } = this.props;
    const { totalCount, size, groupBy, loading } = collection;

    // don't try to load anymore if the collection is fully loaded or is currently loading
    if (loading || (totalCount && Number(size) >= Number(totalCount) && !collection.hasMoreResults)) {
      return Promise.resolve();
    }

    // if the collection is grouped, just pass the collection size as the next index to load
    if (groupBy) {
      startIndex = size;
    }

    return collection.loadMoreItems({ startIndex, stopIndex });
  };

  /**
   * @param {{scrollTop: number}} args
   * @returns {void}
   */
  handleScroll = ({ scrollTop }) => {
    if (scrollTop === 0) {
      this.setStickyHeader(null, {});
    }
  };

  /**
   * @param {{startIndex: number, stopIndex: number}} args
   * @returns {void}
   */
  notifyVisibleRows = ({ startIndex, stopIndex }) => {
    const { collection, modelField, showTotalRow, stickyTotalRow } = this.props;
    this.virtualizedVisibleRowsObserver.emitVisibleRows({
      startIndex,
      stopIndex,
      collection,
      modelField,
      showTotalRow: showTotalRow && !stickyTotalRow,
      getGroupedRow: this.getGroupedRow,
      groupedRowsLength: this.groupedRows.length
    });
  };

  /**
   * @param {{index: number, key: string, style: object, isVisible: boolean}} args
   * @returns {React.ReactNode}
   */
  renderRow = ({ index, key, style, isVisible }) => {
    const { collection, modelField, showTotalRow, stickyTotalRow, stickyHeader, rowKeyField } = this.props;
    const columnWidthsRef = this.getColumnWidthsRef();
    const resizedColumns = this.getResizedColumns();
    const { visibleColumns } = this.state;

    if (collection.isTreeGroupBy) {
      const row = this.getGroupedRow(index);
      if (!row) {
        return null;
      }

      const { model, isExpanded, onToggle, hasChildren } = row;

      return (
        <Row
          {...this.props}
          key={rowKeyField ? model.get(rowKeyField) : model.id}
          model={model}
          style={style}
          updateGrid={this.updateGrid}
          selected={this.isRowSelected(model)}
          disabled={this.isRowDisabled(model)}
          columns={visibleColumns}
          isExpanded={isExpanded}
          onToggle={onToggle}
          hasChildren={hasChildren}
          virtualized
          resizedColumns={resizedColumns}
          columnWidthsRef={columnWidthsRef}
        />
      );
    }

    let model = index < collection.size ? collection.at(index) : null;
    if (modelField) {
      model = collection[modelField][index];
    }
    let rowKeyPrefix = '';

    if (collection.groupBy) {
      const row = this.getGroupedRow(index);

      if (row) {
        model = row.model;
        rowKeyPrefix = `${row.groupKey}-`;

        if (stickyHeader) {
          // For the first item visible, let's make sure we get a group header
          if (!this.isRenderingVisibleRows && isVisible) {
            let stickyModel = row;
            for (let i = index - 1; i >= 0 && stickyModel && !stickyModel.isGroupSummary; i -= 1) {
              stickyModel = this.getGroupedRow(i);
            }
            if (stickyModel && stickyModel.isGroupSummary) {
              if (this.stickyHeaderModel !== stickyModel) {
                this.setStickyHeader(stickyModel, { ...this.props, ...stickyModel, key, style });
              }
            }
          }
          this.isRenderingVisibleRows = isVisible;
        }

        if (row.isGroupSummary) {
          return <GroupRow {...this.props} {...row} key={row.groupKey} style={style} />;
        }
      }
    } else if (stickyHeader && this.stickyHeaderModel) {
      this.setStickyHeader(null, {});
    }

    if (!model) {
      if (showTotalRow && !stickyTotalRow) {
        return this.renderTotals({ key, style });
      }
      return null;
    }

    return (
      <Row
        {...this.props}
        key={rowKeyPrefix + (rowKeyField ? model.get(rowKeyField) : model.id)}
        model={model}
        style={style}
        updateGrid={this.updateGrid}
        selected={this.isRowSelected(model)}
        disabled={this.isRowDisabled(model)}
        columns={visibleColumns}
        virtualized
        resizedColumns={resizedColumns}
        columnWidthsRef={columnWidthsRef}
      />
    );
  };

  /**
   * @param {object} [options]
   * @returns {React.ReactNode}
   */
  renderTotals(options = {}) {
    const { collection, showTotalRow } = this.props;
    const { visibleColumns } = this.state;

    const resizedColumns = this.getResizedColumns();
    const columnWidthsRef = this.getColumnWidthsRef();

    if (showTotalRow && !collection.isRequestActive('fetching') && collection.models.length > 0) {
      return (
        <TotalRow
          {...this.props}
          {...options}
          columns={visibleColumns}
          resizedColumns={resizedColumns}
          columnWidthsRef={columnWidthsRef}
        />
      );
    }

    return null;
  }

  /**
   * @returns {React.ReactNode}
   */
  renderBody() {
    const {
      bodyStyle,
      collection,
      modelField,
      flexed,
      className,
      showTotalRow,
      stickyTotalRow,
      stickyHeader,
      infiniteScroll,
      loading,
      scrollToIndex,
      scrollToAlignment
    } = this.props;

    const style = Object.assign(
      {},
      flexed ? { flex: 1 } : {},
      stickyTotalRow ? { position: 'relative' } : {},
      bodyStyle
    );

    this.isRenderingVisibleRows = false;

    if (collection.isRequestActive('fetching') || collection.size === 0 || loading) {
      return super.renderBody({ style });
    }

    const groupedSize = this.groupedRows.length;
    const fullSize = collection.size;
    let rowCount = fullSize;
    if (collection.groupBy) {
      rowCount = groupedSize;
    } else if (modelField) {
      rowCount = collection[modelField].length;
    }

    if (showTotalRow && !stickyTotalRow) {
      rowCount += 1;
    }

    const listProps = {
      noRowsRenderer: () => this.renderEmpty(),
      rowCount,
      rowHeight: this.getRowHeight,
      rowRenderer: this.renderRow,
      ref: this.listRefHandler,
      width: 100 /* placeholder value - we're injecting 100% via css */,
      onScroll: stickyHeader ? this.handleScroll : undefined,
      scrollToIndex,
      scrollToAlignment,
      onRowsRendered: this.notifyVisibleRows
    };

    if (!infiniteScroll) {
      return (
        <div className={classNames('tbody', className)} style={style}>
          <AutoSizer disableWidth>
            {({ height }) => <List height={this.getContentHeight(height)} {...listProps} />}
          </AutoSizer>
          {stickyHeader && <StickyHeader headerProps={this.stickyHeaderProps} />}
          {stickyTotalRow && this.renderStickyTotals()}
        </div>
      );
    }

    return (
      <div className={classNames('tbody', className)} style={style}>
        <AutoSizer disableWidth>
          {({ height }) => (
            <InfiniteLoader
              isRowLoaded={this.isItemLoaded}
              threshold={50}
              minimumBatchSize={50}
              rowCount={
                // eslint-disable-next-line no-nested-ternary
                collection.hasMoreResults
                  ? Infinity
                  : collection.groupBy || modelField
                    ? parseInt(rowCount)
                    : parseInt(collection.totalCount)
              }
              loadMoreRows={this.loadMoreItems}
            >
              {({ onRowsRendered, ref }) => (
                <List
                  height={this.getContentHeight(height)}
                  {...listProps}
                  ref={ref}
                  onRowsRendered={(range) => {
                    onRowsRendered(range);
                    this.notifyVisibleRows(range);
                  }}
                />
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
        {stickyHeader && <StickyHeader headerProps={this.stickyHeaderProps} />}
        {stickyTotalRow && this.renderStickyTotals()}
      </div>
    );
  }

  renderStickyTotals() {
    if (!this.shouldRenderStickyTotals()) {
      return null;
    }

    const { visibleColumns } = this.state;
    const resizedColumns = this.getResizedColumns();
    const columnWidthsRef = this.getColumnWidthsRef();

    const stickyStyle = {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      paddingRight: getScrollbarWidth(),
      height: this.getStickyFooterHeight()
    };

    return (
      <TotalRow
        {...this.props}
        style={stickyStyle}
        columns={visibleColumns}
        resizedColumns={resizedColumns}
        columnWidthsRef={columnWidthsRef}
      />
    );
  }

  /**
   * @returns {React.ReactNode}
   */
  render() {
    const {
      className,
      headerRef,
      collection,
      flexed,
      headerStyle,
      hideHeader,
      minimal,
      noHighlightSorted,
      onRowClick,
      onSelectAll,
      onSort,
      persistColumnWidths,
      selectOnRowClick,
      tabPrefId,
      useSortTooltips
    } = this.props;
    const { visibleColumns } = this.state;

    const resizedColumns = this.getResizedColumns();
    const columnWidthsRef = this.getColumnWidthsRef();

    const tableClassName = classNames(Classes.HTML_TABLE, className, {
      [Classes.INTERACTIVE]: !minimal && (selectOnRowClick || onRowClick)
    });

    const style = Object.assign(
      { overflow: 'auto hidden', display: 'grid', gridTemplateRows: hideHeader ? '1fr' : 'auto 1fr' },
      flexed ? { flex: 1 } : {},
      this.props.style
    );

    const ariaProps = formAriaProps(
      this.props,
      {},
      {
        passOnExistingAria: true
      }
    );
    if (collection && collection.size) {
      ariaProps['aria-rowcount'] = collection.size;
    }
    if (visibleColumns && visibleColumns.length) {
      ariaProps['aria-colcount'] = visibleColumns.length;
    }

    return (
      <div style={style} className={tableClassName} role="table" {...ariaProps}>
        {!hideHeader && (
          <Header
            headerRef={headerRef}
            useSortTooltips={useSortTooltips}
            noHighlightSorted={noHighlightSorted}
            collection={collection}
            columns={visibleColumns}
            style={{ paddingRight: getScrollbarWidth(), ...headerStyle }}
            onSort={onSort}
            onSelectAll={onSelectAll}
            resizedColumns={resizedColumns}
            tabPrefId={tabPrefId}
            persistColumnWidths={persistColumnWidths}
            updateGrid={this.updateGrid}
            columnWidthsRef={columnWidthsRef}
          />
        )}
        {this.renderBody()}
      </div>
    );
  }
}
